import { useMemo, useState, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { Box, Flex, Text, Heading, VStack, Container } from "@chakra-ui/react"
import { ArrowLeft, Trophy, MapPin } from "lucide-react"
import { useToast } from "@/lib/hooks/use-toast"
import { getGameCourse } from "@/lib/academy/game-mock-data"
import { GameMapNode } from "@/src/components/academy/game/game-map-node"
import { GameMapPath } from "@/src/components/academy/game/game-map-path"
import type { GameChapterItem, GameLevelStatus, GameCourseProgress } from "@zrt-ai-office/types/course"

export function GameMapPage() {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()

  const numericCourseId = Number(courseId)
  const course = getGameCourse(numericCourseId)

  /** Read saved progress from localStorage (matches GameCourseProgress format) */
  const [savedProgress, setSavedProgress] = useState<Record<number, { completed: boolean; stars: number }>>({})

  useEffect(() => {
    function readProgress() {
      if (!courseId) return
      try {
        const raw = localStorage.getItem(`game-progress-${courseId}`)
        if (raw) {
          const parsed = JSON.parse(raw) as GameCourseProgress
          setSavedProgress(parsed.chapters ?? {})
          return
        }
      } catch {
        /* ignore */
      }
      setSavedProgress({})
    }

    readProgress()
  }, [courseId, location.key])

  /** Merge saved progress into chapter status */
  const chapters: GameChapterItem[] = useMemo(() => {
    if (!course) return []

    const sorted = [...course.chapters].sort((a, b) => a.sortOrder - b.sortOrder)

    return sorted.map((ch, idx): GameChapterItem => {
      const progress = savedProgress[ch.id]

      let status: GameLevelStatus = "locked"
      if (idx === 0) {
        /* First level is always at least available */
        status = progress?.completed ? "completed" : "available"
      } else {
        const prevChapter = sorted[idx - 1]
        const prevProgress = savedProgress[prevChapter.id]
        if (progress?.completed) {
          status = "completed"
        } else if (prevProgress?.completed) {
          status = "available"
        } else {
          status = "locked"
        }
      }

      return {
        ...ch,
        status,
        stars: progress?.stars ?? ch.stars,
      }
    })
  }, [course, savedProgress])

  const completedCount = chapters.filter((ch) => ch.status === "completed").length

  /** Handle node click */
  const handleNodeClick = (chapterId: number) => {
    const chapter = chapters.find((ch) => ch.id === chapterId)
    if (!chapter) return

    if (chapter.status === "locked") {
      toast.info("请先通过前面的关卡")
      return
    }

    navigate(`/academy/game/${courseId}/${chapterId}`)
  }

  /* ── Not found ── */
  if (!course) {
    return (
      <Flex h="100dvh" align="center" justify="center" pt="14">
        <Text color="gray.400">课程不存在</Text>
      </Flex>
    )
  }

  return (
    <Box minH="100dvh" bg="slate.50" pt="14">
      {/* ── Header ── */}
      <Box
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.200"
        position="sticky"
        top="56px"
        zIndex={20}
      >
        <Container maxW="4xl" py="3" px="4">
          <Flex align="center" justify="space-between">
            <Flex align="center" gap="3">
              <Box
                as="button"
                onClick={() => navigate("/academy")}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="8"
                h="8"
                borderRadius="lg"
                bg="gray.100"
                color="gray.500"
                transition="all 0.15s"
                _hover={{ bg: "gray.200", color: "gray.700" }}
              >
                <ArrowLeft size={16} />
              </Box>
              <Box>
                <Heading as="h1" fontSize="md" fontWeight="bold" color="slate.900">
                  {course.title}
                </Heading>
                <Flex align="center" gap="1.5" mt="0.5">
                  <Trophy size={12} color="#10b981" />
                  <Text fontSize="xs" color="gray.500">
                    已通关 {completedCount} / {chapters.length}
                  </Text>
                </Flex>
              </Box>
            </Flex>

            {/* Progress indicator */}
            <Flex align="center" gap="2">
              <Box
                w="100px"
                h="6px"
                bg="gray.200"
                borderRadius="full"
                overflow="hidden"
              >
                <Box
                  h="full"
                  bg="emerald.500"
                  borderRadius="full"
                  transition="width 0.5s ease"
                  w={
                    chapters.length > 0
                      ? `${Math.round((completedCount / chapters.length) * 100)}%`
                      : "0%"
                  }
                />
              </Box>
              <Text fontSize="xs" fontWeight="semibold" color="emerald.600">
                {chapters.length > 0
                  ? `${Math.round((completedCount / chapters.length) * 100)}%`
                  : "0%"}
              </Text>
            </Flex>
          </Flex>
        </Container>
      </Box>

      {/* ── Map area ── */}
      <Container maxW="md" py="10" px="4">
        {/* Start marker */}
        <Flex justify="center" mb="6">
          <Flex
            align="center"
            gap="1.5"
            bg="emerald.50"
            color="emerald.600"
            px="4"
            py="2"
            borderRadius="full"
            fontSize="sm"
            fontWeight="semibold"
          >
            <MapPin size={14} />
            <Text>冒险开始</Text>
          </Flex>
        </Flex>

        {/* Level nodes with paths */}
        <VStack gap="0" align="center">
          {chapters.map((chapter, idx) => {
            const isLeft = idx % 2 === 0
            const showPath = idx < chapters.length - 1

            return (
              <Box key={chapter.id} w="full">
                <GameMapNode
                  chapter={chapter}
                  index={idx}
                  isLeft={isLeft}
                  onClick={handleNodeClick}
                />

                {showPath && (
                  <GameMapPath
                    fromLeft={isLeft}
                    toLeft={!(isLeft)}
                    isCompleted={chapter.status === "completed"}
                  />
                )}
              </Box>
            )
          })}
        </VStack>

        {/* End marker */}
        <Flex justify="center" mt="8" mb="4">
          <Flex
            align="center"
            gap="1.5"
            bg={completedCount === chapters.length ? "emerald.100" : "gray.100"}
            color={completedCount === chapters.length ? "emerald.700" : "gray.400"}
            px="4"
            py="2"
            borderRadius="full"
            fontSize="sm"
            fontWeight="semibold"
          >
            <Trophy size={14} />
            <Text>
              {completedCount === chapters.length ? "全部通关!" : "终点"}
            </Text>
          </Flex>
        </Flex>
      </Container>
    </Box>
  )
}
